var React = require('react');
var { createElement: h, useEffect, useRef, useState } = React;
var { request } = require('../lib/apiClient');
var { parseFindings, LogChatFindings } = require('./logChatFindings');
var { renderChatMarkdown } = require('../lib/renderChatMarkdown');

// Shared markdown renderer with the 'log-chat' class prefix so existing
// log-chat-md-* CSS rules continue to apply unchanged.
function renderMarkdown(text) {
  return renderChatMarkdown(text, 'log-chat');
}

function LogChatPopup(props) {
  var PREDEFINED_LOG_PROMPTS = [
    'Inspect the workflow execution.'
  ];
  var workflowId = props.workflowId || '';
  var onClose = props.onClose || function() {};
  var onLocate = props.onLocate || null;
  var onFix = props.onFix || null;
  var [messages, setMessages] = useState([
    { role: 'system', text: 'Ask me anything about the last workflow run \u2014 timings, errors, or how to improve it.' }
  ]);
  var [input, setInput] = useState('');
  var [loading, setLoading] = useState(false);
  var [dismissedByMsg, setDismissedByMsg] = useState({});
  var bottomRef = useRef(null);
  var inputRef = useRef(null);

  function dismissFinding(msgIndex, findingIndex) {
    setDismissedByMsg(function(prev) {
      var next = Object.assign({}, prev);
      var forMsg = Object.assign({}, next[msgIndex] || {});
      forMsg[findingIndex] = true;
      next[msgIndex] = forMsg;
      return next;
    });
  }

  function renderAssistant(text, msgIndex) {
    var findings = parseFindings(text);
    if (findings) {
      return h(LogChatFindings, {
        findings: findings,
        dismissed: dismissedByMsg[msgIndex] || {},
        onDismiss: function(findingIndex) { dismissFinding(msgIndex, findingIndex); },
        onLocate: onLocate,
        onFix: onFix
      });
    }
    return renderMarkdown(text);
  }

  useEffect(function() {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  function sendMessage(text) {
    if (!text || loading) return;
    var history = messages
      .filter(function(m) { return m.role === 'user' || m.role === 'assistant'; })
      .map(function(m) { return { role: m.role, content: m.text }; });
    setMessages(function(prev) { return prev.concat([{ role: 'user', text: text }]); });
    setLoading(true);
    request('/workflow/' + encodeURIComponent(workflowId) + '/log-chat', {
      method: 'POST',
      body: { message: text, history: history }
    }).then(function(res) {
      var reply = (res && res.reply) ? String(res.reply) : '(no response)';
      setMessages(function(prev) { return prev.concat([{ role: 'assistant', text: reply }]); });
    }).catch(function(err) {
      var errMsg = err && err.message ? err.message : 'Request failed';
      setMessages(function(prev) { return prev.concat([{ role: 'assistant', text: 'Error: ' + errMsg }]); });
    }).finally(function() {
      setLoading(false);
    });
  }

  function send() {
    var text = input.trim();
    if (!text || loading) return;
    setInput('');
    sendMessage(text);
  }

  function usePrompt(prompt) {
    sendMessage(String(prompt || '').trim());
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  function stopProp(e) { e.stopPropagation(); }
  return h('div', { className: 'log-chat-popup', onMouseDown: stopProp, onTouchStart: stopProp, onClick: stopProp },
    h('div', { className: 'log-chat-header' },
      h('div', { className: 'log-chat-title' },
        h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
          h('path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }),
          h('polyline', { points: '14 2 14 8 20 8' }),
          h('line', { x1: 16, y1: 13, x2: 8, y2: 13 }),
          h('line', { x1: 16, y1: 17, x2: 8, y2: 17 }),
          h('polyline', { points: '10 9 9 9 8 9' })
        ),
        'Runtime Log Chat'
      ),
      h('button', { className: 'log-chat-close', onClick: onClose, title: 'Close' },
        h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round' },
          h('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
          h('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
        )
      )
    ),
    h('div', { className: 'log-chat-messages' },
      messages.map(function(m, i) {
        return h('div', {
          key: i,
          className: 'log-chat-msg log-chat-msg-' + m.role
        }, m.role === 'assistant' ? renderAssistant(m.text, i) : m.text);
      }),
      loading ? h('div', { className: 'log-chat-typing' },
        h('span'), h('span'), h('span')
      ) : null,
      h('div', { ref: bottomRef })
    ),
    h('div', { className: 'log-chat-input-row' },
      h('button', {
        type: 'button',
        className: 'log-chat-preset-btn',
        onClick: function() { usePrompt(PREDEFINED_LOG_PROMPTS[0]); },
        disabled: loading,
        title: PREDEFINED_LOG_PROMPTS[0]
      }, PREDEFINED_LOG_PROMPTS[0]),
      h('textarea', {
        ref: inputRef,
        className: 'log-chat-input',
        placeholder: 'Ask about the log\u2026',
        value: input,
        onChange: function(e) { setInput(e.target.value); },
        onKeyDown: onKeyDown,
        rows: 1,
        disabled: loading
      }),
      h('button', {
        className: 'log-chat-send',
        onClick: send,
        disabled: loading || !input.trim(),
        title: 'Send'
      },
        h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' },
          h('line', { x1: 22, y1: 2, x2: 11, y2: 13 }),
          h('polygon', { points: '22 2 15 22 11 13 2 9 22 2' })
        )
      )
    )
  );
}

module.exports = LogChatPopup;
